import { useEffect, useState } from "react"

import { Link as LinkIcon } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"

import {
  handleApiError,
  ordersService,
  type JunctionRelinkCandidate,
  type Order,
} from "../../api"
import { formatDateTime } from "../data-grid-utils"

type RelinkJunctionModalProps = {
  open: boolean
  onClose: () => void
  orderId: string
  onLinked: (updatedOrder: Order) => void
}

type RenderBodyArgs = {
  isLoading: boolean
  candidates: JunctionRelinkCandidate[]
  linkingId: string | null
  onLink: (junctionOrderId: string) => void
}

const renderBody = ({ isLoading, candidates, linkingId, onLink }: RenderBodyArgs) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (candidates.length === 0) {
    return <Alert severity="info">No Junction orders found for this user.</Alert>
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Created</TableCell>
            <TableCell>Lab Test</TableCell>
            <TableCell>Junction Status</TableCell>
            <TableCell>Junction Order ID</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {candidates.map((c) => (
            <TableRow key={c.junctionOrderId}>
              <TableCell>{formatDateTime(c.createdAt)}</TableCell>
              <TableCell>{c.labTestName}</TableCell>
              <TableCell>
                <Chip
                  label={c.status.toUpperCase()}
                  size="small"
                  variant="outlined"
                  color={c.status === "completed" ? "success" : "default"}
                />
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                {c.junctionOrderId}
              </TableCell>
              <TableCell align="right">
                {c.linkedToOrderId !== null ? (
                  <Typography variant="caption" color="text.secondary">
                    linked to <code>{c.linkedToOrderId.slice(0, 8)}…</code>
                  </Typography>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      linkingId === c.junctionOrderId ? (
                        <CircularProgress size={14} />
                      ) : (
                        <LinkIcon />
                      )
                    }
                    disabled={linkingId !== null}
                    onClick={() => onLink(c.junctionOrderId)}
                  >
                    Link
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export function RelinkJunctionModal({
  open,
  onClose,
  orderId,
  onLinked,
}: RelinkJunctionModalProps) {
  const [candidates, setCandidates] = useState<JunctionRelinkCandidate[]>([])
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false)
  const [linkingId, setLinkingId] = useState<string | null>(null)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!open) return

    const fetchCandidates = async () => {
      try {
        setIsLoadingCandidates(true)
        setError("")
        const result = await ordersService.getJunctionRelinkCandidates(orderId)
        setCandidates(result.candidates)
      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setIsLoadingCandidates(false)
      }
    }

    fetchCandidates()
  }, [open, orderId])

  const handleLink = async (junctionOrderId: string) => {
    try {
      setLinkingId(junctionOrderId)
      setError("")
      const updated = await ordersService.relinkJunctionOrder(
        orderId,
        junctionOrderId,
      )
      onLinked(updated)
      onClose()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setLinkingId(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Link Junction Order</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick a Junction order belonging to this user. Linking will set
          <code> junction_order_id </code> on this row and sync status from
          Junction (which will also trigger AI report generation if Junction
          reports the order as completed).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderBody({
          isLoading: isLoadingCandidates,
          candidates,
          linkingId,
          onLink: handleLink,
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={linkingId !== null}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
